/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useFarmStore } from '@/stores/farmStore';

export interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const hadSession = useRef(false);

  // Any sign-out (button, another tab, revoked token): the next login in this tab starts
  // clean. Runs after the commit that unmounts the app (AppLayout redirects when the
  // session is gone), so no mounted query refires into the signed-out session.
  useEffect(() => {
    if (session) {
      hadSession.current = true;
      return;
    }
    if (loading || !hadSession.current) return;
    hadSession.current = false;
    useFarmStore.setState({ selectedFarmId: null });
    useFarmStore.persist.clearStorage();
    queryClient.clear();
  }, [session, loading, queryClient]);

  useEffect(() => {
    // Issue #109: three fixes rolled into one refactor:
    //   (a) If `getSession()` resolves AFTER an `onAuthStateChange(SIGNED_OUT)`
    //       fires (e.g. sign-out in another tab during initial load), the
    //       getSession() callback would overwrite the signed-out state —
    //       leaving a stale session. Guard with `hasInitialized` so only the
    //       FIRST event wins, and onAuthStateChange events after init keep
    //       priority.
    //   (b) `loading` never returned to `true` after the initial fetch; that
    //       was intentional but it meant sign-out followed by sign-in didn't
    //       re-indicate loading. Now controlled by the `hasInitialized` flag
    //       separately.
    //   (c) Cancel state updates after unmount so we don't set state on a
    //       dead component during tab teardown.
    let alive = true;
    let hasInitialized = false;

    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (!alive || hasInitialized) return;
      hasInitialized = true;
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!alive) return;
      hasInitialized = true;
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setLoading(false);
    });

    return () => {
      alive = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ session, user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
